import classNames from 'classnames';
import { Menu, TextInputProps } from 'lego-on-react';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useToggle } from '../../hooks/common';
import { Bem } from '../../service/Bem/Bem';
import { DictionaryItem } from '../../typings/common';
import { ScopedPopup } from '../ScopedPopup/ScopedPopup';
import { TextInputDecorator, TextInputDecoratorProps } from '../TextInputDecorator/TextInputDecorator';

import './SuggestDecorator.css';

const BLOCK = 'suggest-decorator';

export interface SuggestDecoratorProps extends TextInputDecoratorProps {
    popupCls?: string;
    suggestItems?: DictionaryItem[];
    text?: string;
    onSelect?: (item: DictionaryItem) => void;
    onPaste?: (evt: React.ClipboardEvent<HTMLInputElement>) => void;
    onRefChange?: (ref: React.RefObject<TextInputDecorator>) => void;
    showListOnFocus?: boolean;
    sizeByContent?: boolean;
}

export const SuggestDecorator: React.FC<SuggestDecoratorProps> = React.memo(props => {
    const {
        text = '',
        suggestItems,
        showListOnFocus = false,
        onFocus,
        onBlur,
        onSelect,
        onChange,
        onPaste,
        onKeyDown,
        onRefChange,
        autoFocus,
        sizeByContent,
        controlAttrs,
        popupCls,
        ...inputProps
    } = props;

    const [isOpen, open, close] = useToggle();
    const [isFocused, focus, blur] = useToggle();
    const inputRef = useRef<TextInputDecorator>(null);
    const suggestControlAttrs: TextInputProps['controlAttrs'] = {
        ...controlAttrs
    };

    if (sizeByContent) {
        suggestControlAttrs.size = (text.length || 0) + 2;
    }

    suggestControlAttrs.onPaste = onPaste;

    const lowerCaseText = text.trim().toLowerCase();
    const [filteredItems, setFilteredItems] = useState<DictionaryItem[]>(
        text && suggestItems ? suggestItems.filter(item => item.text.toLowerCase().includes(lowerCaseText)) : []
    );

    const onInputFocus = useCallback(
        (evt: React.FocusEvent<HTMLInputElement>) => {
            focus();

            if (onFocus) {
                onFocus(evt);
            }
        },
        [onFocus, focus]
    );

    const onInputBlur = useCallback(
        (evt: React.FocusEvent<HTMLInputElement>) => {
            blur();

            if (onBlur) {
                onBlur(evt);
            }
        },
        [onBlur, blur]
    );

    const onItemSelect = useCallback(
        (evt: React.MouseEvent<HTMLDivElement>, value: string) => {
            evt.preventDefault();
            evt.stopPropagation();
            const selectedItem = suggestItems && suggestItems.find(item => item.val === value);

            if (onSelect && selectedItem) {
                onSelect(selectedItem);
            }

            if (autoFocus && inputRef && inputRef.current) {
                inputRef.current.focus();
            }
        },
        [onSelect, suggestItems]
    );

    const onInputKeyDown = useCallback(
        (evt: React.KeyboardEvent<HTMLInputElement>) => {
            if (onKeyDown) {
                onKeyDown(evt);
            }

            if (text.length > 0 && suggestItems && suggestItems.length > 0) {
                open();
                const textInLowerCase = text.toLowerCase();

                setFilteredItems(suggestItems.filter(item => item.text.toLowerCase().includes(textInLowerCase)));
            }
        },
        [text, suggestItems]
    );

    // ДАЛЕЕ идут сайд-эффекты

    // При получении нового списка опций для саджеста мы ставим фокус
    // на поле ввода, если включён автофокус
    useEffect(() => {
        if (autoFocus && inputRef && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus, suggestItems]);

    useEffect(() => {
        if (suggestItems) {
            setFilteredItems(suggestItems.filter(item => item.text.toLowerCase().includes(lowerCaseText)));

            if (!isOpen) {
                open();
            }
        }
    }, [suggestItems, lowerCaseText, showListOnFocus, isOpen]);

    // Открываем попап при фокусе, если это требуется
    useEffect(() => {
        if (showListOnFocus && isFocused && suggestItems && suggestItems.length > 0) {
            open();
        }
    }, [isFocused, showListOnFocus, suggestItems]);

    // Закрываем попап, если пришедший список опций пустой
    useEffect(() => {
        if (isOpen && (!suggestItems || suggestItems.length === 0)) {
            close();
        }
    }, [isOpen, suggestItems]);

    // Пробрасываем ref TextInputDecorator наверх, если он там нужен
    useEffect(() => {
        if (inputRef && onRefChange) {
            onRefChange(inputRef);
        }
    }, [inputRef]);

    const onMouseDown = useCallback((evt: React.MouseEvent<HTMLElement>) => {
        evt.preventDefault();
    }, []);

    return (
        <>
            <TextInputDecorator
                text={text}
                ref={inputRef}
                onChange={onChange}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                onKeyDown={onInputKeyDown}
                autoFocus={autoFocus}
                controlAttrs={suggestControlAttrs}
                {...inputProps}
            />
            <ScopedPopup
                hiding={true}
                theme={'normal'}
                visible={isOpen && isFocused && filteredItems.length > 0}
                target={'anchor'}
                anchor={inputRef.current}
                directions={['bottom-left']}
                onOutsideClick={close}
                cls={classNames(Bem.cls(BLOCK, 'popup'), popupCls)}
            >
                <Menu
                    cls={Bem.cls(BLOCK, 'popup-menu')}
                    theme="normal"
                    type="simple"
                    items={filteredItems}
                    size={'n'}
                    val={text}
                    onClick={onItemSelect}
                    onMouseDown={onMouseDown}
                />
            </ScopedPopup>
        </>
    );
});
